// The Authoring-route agent switcher (docs/09 §4, UI-LAW §8). Creation routes
// (`/simulations/new`, `/markers/new`) author against exactly ONE agent, so the
// scope lives in the PageHeader `leading` slot as a quiet sm Select instead of
// a dead-end "select an agent first" empty state.
//
// It is bound to the ROOT-owned `?agent=` search param — never local form
// state — so scope stays single-sourced: deep links, the sidebar selector, and
// the browser back button all stay in agreement.

import type { ReactNode } from "react";

import { queryKeys } from "../lib/queryKeys";
import { useListQuery } from "../lib/useListQuery";
import { useResolvedWorkingTeamId, useWorkingScope } from "../lib/workingScope";
import { ScopePicker, Select, ShellIcon, type ShellIconName } from "../ui";

export interface AgentScopeOption {
  displayName: string;
  id: string;
}

export interface AgentScopeOptions {
  agents: AgentScopeOption[];
  errorMessage?: string;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
}

// The working team's agents — the SAME query key the shell's agent selector
// uses (`AppLayout`), so mounting this header reads the cache instead of
// issuing a second round trip.
export function useAgentScopeOptions(): AgentScopeOptions {
  const teamId = useResolvedWorkingTeamId();
  const agents = useListQuery("/v1/agents", {
    params: { team_id: teamId },
    queryKey: queryKeys.agents.list({ team_id: teamId }),
  });
  return {
    agents: agents.items.map((agent) => ({
      displayName: agent.display_name,
      id: agent.id,
    })),
    errorMessage: agents.errorMessage,
    fetchNextPage: agents.fetchNextPage,
    hasNextPage: agents.hasNextPage,
    isFetchingNextPage: agents.isFetchingNextPage,
    isPending: agents.isPending,
  };
}

export interface AgentScopePickerProps {
  // Rendered when the org has no agents at all — a single create CTA.
  emptyState?: ReactNode;
  // The destination surface as the breadcrumb names it: "Simulations".
  surfaceLabel: string;
  surfaceIcon: ShellIconName;
}

// The page BODY when a required agent scope is missing: `ScopePicker` wired to
// the same agents read the header switcher uses, so the choices are ON the page
// (no bounce to the sidebar). Selection patches root-owned `?agent=`.
export function AgentScopePicker({
  emptyState,
  surfaceIcon,
  surfaceLabel,
}: AgentScopePickerProps) {
  const { selectAgent } = useWorkingScope();
  const {
    agents,
    errorMessage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useAgentScopeOptions();

  return (
    <ScopePicker
      emptyState={emptyState}
      errorMessage={errorMessage}
      hasNextPage={hasNextPage}
      icon={<ShellIcon name={surfaceIcon} />}
      isFetchingNextPage={isFetchingNextPage}
      isPending={isPending}
      onLoadMore={fetchNextPage}
      onSelect={selectAgent}
      options={agents.map((agent) => ({
        id: agent.id,
        label: agent.displayName,
      }))}
      scopeNoun="agent"
      surfaceLabel={surfaceLabel}
    />
  );
}

export interface AgentScopeSelectProps {
  // Intercepts the scope change so an Authoring route can route it through its
  // own leave guard. Defaults to patching `?agent=` directly.
  onSelectAgent?: (agentId: string) => void;
}

export function AgentScopeSelect({ onSelectAgent }: AgentScopeSelectProps) {
  const { agentId, selectAgent } = useWorkingScope();
  const { agents } = useAgentScopeOptions();

  return (
    <AgentScopeSelectView
      agentId={agentId}
      agents={agents}
      onSelectAgent={onSelectAgent ?? selectAgent}
    />
  );
}

export interface AgentScopeSelectViewProps {
  agentId?: string;
  agents: AgentScopeOption[];
  onSelectAgent: (agentId: string) => void;
}

// Props-pure view (container/view split): the quiet sm select the PageHeader
// `leading` slot renders. A long display name ellipsizes and keeps the full
// name on the trigger's native `title`.
export function AgentScopeSelectView({
  agentId,
  agents,
  onSelectAgent,
}: AgentScopeSelectViewProps) {
  return (
    <Select
      disabled={agents.length === 0}
      label="Agent"
      labelHidden
      required
      size="sm"
      value={agentId ?? ""}
      onChange={(event) => {
        const nextAgentId = event.currentTarget.value;
        if (nextAgentId === "" || nextAgentId === agentId) {
          return;
        }
        onSelectAgent(nextAgentId);
      }}
    >
      <option disabled value="">
        Select an agent
      </option>
      {agents.map((agent) => (
        <option key={agent.id} value={agent.id}>
          {agent.displayName}
        </option>
      ))}
    </Select>
  );
}
